import { inject, Injectable } from '@angular/core'
import {
  AnnotationsHearingSectionsPeopleAnnotationsClient,
  CreateHearingSectionPeopleAnnotationRequest,
  HearingSectionPeopleAnnotation,
  UpdateHearingSectionPeopleAnnotationRequest,
} from '@ftr/annotations-contracts'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { ApiResult, RemoteData, tapFailure, unwrapData } from '@ftr/foundation'
import { firstValueFrom } from 'rxjs'
import { AnnotationsApiClientFactory } from '../../api-client'
import { AnnotationsService } from '../base'

@Injectable({
  providedIn: 'root',
})
export class HearingSectionPeopleAnnotationsService extends AnnotationsService {
  constructor() {
    super(inject(AnnotationsApiClientFactory), inject(CourtSystemRegionCache))
  }

  /**
   * @param courtSystemId
   * @param recordingId
   * @param hearingSectionId
   * @param audioSegmentId
   * @returns Hearing section people annotations within a court system. Empty array if feature flag is off
   */
  listHearingSectionPeopleAnnotations({
    courtSystemId,
    recordingId,
    hearingSectionId,
    audioSegmentId,
  }: {
    courtSystemId: Uuid
    recordingId: Uuid
    hearingSectionId?: Uuid
    audioSegmentId?: Uuid
  }): ApiResult<HearingSectionPeopleAnnotation[]> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsHearingSectionsPeopleAnnotationsClient['listPeopleAnnotations']>>,
      HearingSectionPeopleAnnotation[]
    >({
      courtSystemId,
      requestFn: client =>
        client.hearingSectionsPeopleAnnotations.listPeopleAnnotations({
          query: {
            courtSystemId,
            recordingId,
            hearingSectionId,
            audioSegmentId,
          },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to create annotation'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  createHearingSectionPeopleAnnotation(
    body: CreateHearingSectionPeopleAnnotationRequest,
  ): Promise<HearingSectionPeopleAnnotation | undefined> {
    const { hearingSectionId, courtSystemId } = body

    return firstValueFrom(
      this.performApiRequest<
        Awaited<ReturnType<AnnotationsHearingSectionsPeopleAnnotationsClient['createPeopleAnnotation']>>,
        HearingSectionPeopleAnnotation
      >({
        courtSystemId,
        requestFn: client =>
          client.hearingSectionsPeopleAnnotations.createPeopleAnnotation({
            params: { hearingSectionId },
            body,
          }),
        responseMapFn: result => {
          if (result.status !== 201) {
            return RemoteData.failure(new Error('Failed to create annotation'))
          }

          return RemoteData.success(result.body)
        },
      }).pipe(
        tapFailure(err => {
          throw err
        }),
        unwrapData(),
      ),
    )
  }

  updateHearingSectionPeopleAnnotation(
    body: UpdateHearingSectionPeopleAnnotationRequest & Pick<HearingSectionPeopleAnnotation, 'hearingSectionId' | 'id'>,
  ): ApiResult<HearingSectionPeopleAnnotation | undefined> {
    const { hearingSectionId, courtSystemId, id } = body

    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsHearingSectionsPeopleAnnotationsClient['updatePeopleAnnotation']>>,
      HearingSectionPeopleAnnotation
    >({
      courtSystemId,
      requestFn: client =>
        client.hearingSectionsPeopleAnnotations.updatePeopleAnnotation({
          params: { annotationId: id, hearingSectionId },
          body,
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to update annotation'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  deleteHearingSectionPeopleAnnotation(
    body: Pick<HearingSectionPeopleAnnotation, 'courtSystemId' | 'hearingSectionId' | 'id'>,
  ): ApiResult<undefined> {
    const { hearingSectionId, courtSystemId, id } = body

    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsHearingSectionsPeopleAnnotationsClient['deletePeopleAnnotation']>>,
      undefined
    >({
      courtSystemId,
      requestFn: client =>
        client.hearingSectionsPeopleAnnotations.deletePeopleAnnotation({
          params: { annotationId: id, hearingSectionId },
        }),
      responseMapFn: result => {
        if (result.status !== 204) {
          return RemoteData.failure(new Error('Failed to delete annotation'))
        }

        return RemoteData.success(undefined)
      },
    })
  }
}
